import { useState, useCallback } from 'react';
import { useAuthStore } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import { uploadReceipt } from '../services/receiptService';
import {
  generateReceiptId,
  isPdfFile,
  isValidReceiptType,
  type ReceiptFile,
} from '../types';

interface UseReceiptUploadOptions {
  defaultProjectId?: string | null;
  defaultSupplierId?: string | null;
  onUploadStart?: (count: number) => void;
  onUploadComplete?: (successCount: number, errorCount: number, totalCount: number) => void;
  onError?: (error: Error) => void;
}

interface UseReceiptUploadReturn {
  receipts: ReceiptFile[];
  isProcessing: boolean;
  addFiles: (files: FileList | File[]) => void;
  removeReceipt: (receiptId: string) => void;
  setSelectedProject: (receiptId: string, projectId: string | null) => void;
  setSelectedSupplier: (receiptId: string, supplierId: string | null) => void;
  uploadReceipts: () => Promise<void>;
  clearAll: () => void;
}

/**
 * Create a preview URL for an image file (returns null for PDFs)
 */
function createPreviewUrl(file: File): string | null {
  if (isPdfFile(file)) {
    return null;
  }
  return URL.createObjectURL(file);
}

/**
 * Revoke a preview URL to free memory
 */
function revokePreviewUrl(url: string | null): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

export function useReceiptUpload(options: UseReceiptUploadOptions = {}): UseReceiptUploadReturn {
  const {
    defaultProjectId = null,
    defaultSupplierId = null,
    onUploadStart,
    onUploadComplete,
    onError,
  } = options;

  const { user } = useAuthStore();
  const effectiveBusinessId = useEffectiveBusinessId();

  const [receipts, setReceipts] = useState<ReceiptFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;

    const newReceipts: ReceiptFile[] = fileArray
      .filter((file) => {
        if (!isValidReceiptType(file)) {
          console.warn(`Skipping unsupported file type: ${file.type}`);
          return false;
        }
        return true;
      })
      .map((file) => ({
        id: generateReceiptId(),
        file,
        preview: createPreviewUrl(file),
        isPdf: isPdfFile(file),
        status: 'ready' as const,
        selectedProjectId: defaultProjectId,
        selectedSupplierId: defaultSupplierId,
      }));

    setReceipts((prev) => [...prev, ...newReceipts]);
  }, [defaultProjectId, defaultSupplierId]);

  const removeReceipt = useCallback((receiptId: string) => {
    setReceipts((prev) => {
      const receipt = prev.find((r) => r.id === receiptId);
      if (receipt) {
        revokePreviewUrl(receipt.preview);
      }
      return prev.filter((r) => r.id !== receiptId);
    });
  }, []);

  const setSelectedProject = useCallback((receiptId: string, projectId: string | null) => {
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === receiptId ? { ...r, selectedProjectId: projectId } : r
      )
    );
  }, []);

  const setSelectedSupplier = useCallback((receiptId: string, supplierId: string | null) => {
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === receiptId ? { ...r, selectedSupplierId: supplierId } : r
      )
    );
  }, []);

  const uploadReceipts = useCallback(async () => {
    if (!effectiveBusinessId || !user?.user_profile_id) {
      onError?.(new Error('User not authenticated or no business selected'));
      return;
    }

    const readyReceipts = receipts.filter((r) => r.status === 'ready');

    if (readyReceipts.length === 0) return;

    setIsProcessing(true);
    
    // Notify upload start
    onUploadStart?.(readyReceipts.length);

    let successCount = 0;
    let errorCount = 0;

    for (const receipt of readyReceipts) {
      try {
        // Mark as uploading
        setReceipts((prev) =>
          prev.map((r) =>
            r.id === receipt.id ? { ...r, status: 'uploading' as const } : r
          )
        );

        // Use defaultProjectId if set, otherwise use selectedProjectId
        const projectIdToUse = defaultProjectId || receipt.selectedProjectId;
        const supplierIdToUse = defaultSupplierId || receipt.selectedSupplierId;

        // Upload
        await uploadReceipt({
          file: receipt.file,
          businessId: effectiveBusinessId,
          uploadedBy: user.user_profile_id,
          projectId: projectIdToUse,
          supplierId: supplierIdToUse,
        });

        // Mark as uploaded
        setReceipts((prev) =>
          prev.map((r) =>
            r.id === receipt.id ? { ...r, status: 'uploaded' as const } : r
          )
        );

        successCount++;
      } catch (error) {
        console.error(`Error uploading receipt ${receipt.id}:`, error);
        setReceipts((prev) =>
          prev.map((r) =>
            r.id === receipt.id
              ? {
                  ...r,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : r
          )
        );
        errorCount++;
        onError?.(error instanceof Error ? error : new Error('Upload failed'));
      }
    }

    setIsProcessing(false);

    // Notify upload complete with counts
    onUploadComplete?.(successCount, errorCount, readyReceipts.length);
  }, [
    receipts,
    user,
    effectiveBusinessId,
    defaultProjectId,
    defaultSupplierId,
    onUploadStart,
    onUploadComplete,
    onError,
  ]);

  const clearAll = useCallback(() => {
    receipts.forEach((receipt) => revokePreviewUrl(receipt.preview));
    setReceipts([]);
  }, [receipts]);

  return {
    receipts,
    isProcessing,
    addFiles,
    removeReceipt,
    setSelectedProject,
    setSelectedSupplier,
    uploadReceipts,
    clearAll,
  };
}